import React, { useState, useEffect, useCallback, useMemo } from "react";
import { API_BASE_URL } from "../config";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Package, Truck, CreditCard } from "lucide-react";

function ShoppingCart() {
    const [cartItems, setCartItems] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [checkoutSuccess] = useState(false);

    useEffect(() => {
        const fetchCartItems = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_BASE_URL}/api/cart/items`, {
                    headers: { Authorization: token },
                });

                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const data = await response.json();
                setCartItems(data);
            } catch (error) {
                console.error("Error fetching cart items:", error);
            } finally {
                setInitialLoading(false);
            }
        };

        fetchCartItems();
    }, []);

    const totalItems = useMemo(
        () => cartItems.reduce((total, item) => total + item.quantity, 0),
        [cartItems]
    );

    const subtotal = useMemo(
        () => cartItems.reduce((total, item) => total + item.totalAmount, 0),
        [cartItems]
    );

    const shipping = subtotal > 500 ? 0 : 50;
    const total = subtotal + shipping;


    const handleRemoveItem = useCallback(async (orderId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/cart/items/${orderId}`, {
                method: "DELETE",
                headers: { Authorization: token },
            });

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            setCartItems((prevItems) => prevItems.filter((item) => item.orderId !== orderId));
        } catch (error) {
            console.error("Error removing cart item:", error);
            alert("เกิดข้อผิดพลาดในการลบสินค้า");
        }
    }, []);

    const handleIncrementQuantity = useCallback(async (orderId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/cart/items/${orderId}/increase`, {
                method: "POST",
                headers: { Authorization: token },
            });

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            setCartItems((prevItems) =>
                prevItems.map((item) =>
                    item.orderId === orderId
                        ? { ...item, quantity: item.quantity + 1, totalAmount: item.price * (item.quantity + 1) }
                        : item
                )
            );
        } catch (error) {
            console.error("Error increasing quantity:", error);
        }
    }, []);

    const handleDecrementQuantity = useCallback(
        async (orderId) => {
            const item = cartItems.find((item) => item.orderId === orderId);
            if (item?.quantity <= 1) return handleRemoveItem(orderId);

            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_BASE_URL}/api/cart/items/${orderId}/decrease`, {
                    method: "POST",
                    headers: { Authorization: token },
                });

                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                setCartItems((prevItems) =>
                    prevItems.map((item) =>
                        item.orderId === orderId
                            ? { ...item, quantity: item.quantity - 1, totalAmount: item.price * (item.quantity - 1) }
                            : item
                    )
                );
            } catch (error) {
                console.error("Error decreasing quantity:", error);
            }
        },
        [cartItems, handleRemoveItem]
    );

    // Success Screen
    if (checkoutSuccess) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center px-4">
                <div className="bg-white rounded-xl shadow-2xl p-12 text-center max-w-md">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CreditCard className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">สั่งซื้อสำเร็จ!</h2>
                    <p className="text-gray-600 mb-8">ขอบคุณสำหรับการสั่งซื้อ เราจะจัดส่งสินค้าให้คุณโดยเร็วที่สุด</p>
                    <div className="space-y-3">
                        <Link
                            to="/account/orders"
                            className="block w-full py-3 px-6 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all"
                        >
                            ดูรายการคำสั่งซื้อ
                        </Link>
                        <Link
                            to="/all-products"
                            className="block w-full py-3 px-6 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
                        >
                            กลับหน้าแรก
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Loading State
    if (initialLoading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-black rounded-lg">
                        <ShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">ตะกร้าสินค้า</h1>
                        <p className="text-gray-500">{totalItems} รายการ</p>
                    </div>
                </div>

                {cartItems.length === 0 ? (
                    // Empty Cart
                    <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                        <Package className="w-20 h-20 mx-auto text-gray-300 mb-6" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">ตะกร้าสินค้าว่างเปล่า</h2>
                        <p className="text-gray-500 mb-8">คุณยังไม่มีสินค้าในตะกร้า เริ่มช้อปเลย!</p>
                        <Link
                            to="/all-products"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all"
                        >
                            เลือกซื้อสินค้า <ArrowRight size={20} />
                        </Link>
                    </div>
                ) : (
                    <div className="lg:grid lg:grid-cols-12 lg:gap-12">
                        {/* Cart Items */}
                        <section className="lg:col-span-7">
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                {cartItems.map((item, index) => (
                                    <div
                                        key={item.orderId}
                                        className={`flex gap-4 p-6 ${index !== cartItems.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        {/* Image */}
                                        <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={item.ImageUrl ? `${API_BASE_URL}${item.ImageUrl}` : "https://via.placeholder.com/150?text=No+Image"}
                                                alt={item.productName}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=No+Image"; }}
                                            />
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-medium text-gray-900 truncate">{item.productName}</h3>
                                            <p className="text-lg font-bold text-red-600 mt-1">
                                                ฿{Number(item.price).toLocaleString()}
                                            </p>

                                            {/* Quantity Controls */}
                                            <div className="flex items-center gap-4 mt-3">
                                                <div className="flex items-center border border-gray-200 rounded-lg">
                                                    <button
                                                        onClick={() => handleDecrementQuantity(item.orderId)}
                                                        className="p-2 hover:bg-gray-100 transition-colors"
                                                    >
                                                        <Minus size={16} />
                                                    </button>
                                                    <span className="w-10 text-center font-medium">{item.quantity}</span>
                                                    <button
                                                        onClick={() => handleIncrementQuantity(item.orderId)}
                                                        className="p-2 hover:bg-gray-100 transition-colors"
                                                    >
                                                        <Plus size={16} />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveItem(item.orderId)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Item Total */}
                                        <div className="text-right">
                                            <p className="font-bold text-gray-900">
                                                ฿{Number(item.totalAmount).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Order Summary */}
                        <section className="lg:col-span-5 mt-8 lg:mt-0">
                            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">สรุปคำสั่งซื้อ</h2>

                                {/* Price Breakdown */}
                                <div className="space-y-3 pb-6 border-b border-gray-100">
                                    <div className="flex justify-between text-gray-600">
                                        <span>รวมค่าสินค้า</span>
                                        <span>฿{Number(subtotal).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-600">
                                        <span className="flex items-center gap-1">
                                            <Truck size={16} />
                                            ค่าจัดส่ง
                                        </span>
                                        <span>{shipping === 0 ? "ฟรี" : `฿${shipping}`}</span>
                                    </div>
                                </div>

                                {/* Total */}
                                <div className="flex justify-between items-center py-6">
                                    <span className="text-lg font-bold text-gray-900">ยอดสุทธิ</span>
                                    <span className="text-2xl font-bold text-red-600">
                                        ฿{Number(total).toLocaleString()}
                                    </span>
                                </div>

                                {/* Free Shipping Banner */}
                                {subtotal < 500 && (
                                    <div className="bg-red-50 p-4 rounded-lg mb-6">
                                        <p className="text-sm text-red-700 flex items-center gap-2">
                                            <Truck size={16} /> ซื้อเพิ่มอีก ฿{500 - subtotal} ส่งฟรี!
                                        </p>
                                    </div>
                                )}

                                {/* Checkout Button */}
                                <Link
                                    to="/checkout"
                                    className="w-full py-4 px-8 bg-red-600 text-white rounded-lg font-semibold text-lg hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                                >
                                    <CreditCard size={20} />
                                    ดำเนินการสั่งซื้อ
                                </Link>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ShoppingCart;
